import { Autocomplete, CircularProgress, TextField } from "@mui/material"
import { translate } from "app/language/service"
import { messageSVC } from "message/service"
import React from "react"
import {
  IGeoShape,
  IGeoShapeCity,
  IGeoShapeCountry,
  IGeoShapeMetropolitan,
  IGeoShapePostalCode,
  IGeoShapeState,
  MIN_FILTER_LENGTH,
  SHAPE_TYPES
} from "shape/model"
import { getShapeTitle, searchCities, searchCountries, searchPostalCodes, searchStates } from "shape/tools/common"
import { searchMetropolitans } from "shape/tools/metropolitan"
import { FlexBox } from "ui/components/FlexBox"

interface IShapeSelectProps {
  onSelect: (shape: IGeoShape) => any
}

let lastRequestIndex = 0

export const ShapeSelect = React.memo(({ onSelect }: IShapeSelectProps): JSX.Element => {
  const [isLoading, setIsLoading] = React.useState(false)
  const [cities, setCities] = React.useState<readonly IGeoShapeCity[]>([])
  const [postalCodes, setPostalCodes] = React.useState<readonly IGeoShapePostalCode[]>([])
  const [states, setStates] = React.useState<readonly IGeoShapeState[]>([])
  const [countries, setCountries] = React.useState<readonly IGeoShapeCountry[]>([])
  const [metropolitans, setMetropolitans] = React.useState<readonly IGeoShapeMetropolitan[]>([])
  const [filter, setFilter] = React.useState("")
  let timeout: any
  const translationsByType = {
    [SHAPE_TYPES.CITY]: translate("campaign.geo-targets.shape.city.title"),
    [SHAPE_TYPES.POSTAL_CODE]: translate("campaign.geo-targets.shape.postal-code.title"),
    [SHAPE_TYPES.STATE]: translate("campaign.geo-targets.shape.state.title"),
    [SHAPE_TYPES.COUNTRY]: translate("campaign.geo-targets.shape.country.title"),
    [SHAPE_TYPES.METROPOLITAN]: translate("campaign.geo-targets.shape.metropolitan.title")
  }
  const clearData = () => {
    setCities([])
    setPostalCodes([])
    setStates([])
    setCountries([])
    setMetropolitans([])
    setIsLoading(false)
  }
  const loadData = (newFilter: string): Promise<void> =>
    new Promise<void>(resolve => {
      const currentRequest = ++lastRequestIndex
      if (!isLoading) {
        setIsLoading(true)
      }
      let processedCount = 0
      const toProcessCount = 4
      const onFinish = (hasError?: boolean) => {
        if (currentRequest !== lastRequestIndex) {
          return resolve()
        }
        if (hasError) {
          messageSVC.error(translate("campaign.geo-targets.shape.search.error"))
        }
        if (++processedCount === toProcessCount) {
          setIsLoading(false)
          resolve()
        }
      }
      searchCities(newFilter)
        .then(newCities => {
          setCities(newCities)
          setMetropolitans(searchMetropolitans(newFilter))
          onFinish(false)
        })
        .catch(error => {
          console.error(error)
          setMetropolitans(searchMetropolitans(newFilter))
          setCities([])
          onFinish(true)
        })
      searchPostalCodes(newFilter)
        .then(newPostalCodes => {
          setPostalCodes(newPostalCodes)
          onFinish(false)
        })
        .catch(error => {
          console.error(error)
          setPostalCodes([])
          onFinish(true)
        })
      searchStates(newFilter)
        .then(newStates => {
          setStates(newStates)
          onFinish(false)
        })
        .catch(error => {
          console.error(error)
          setStates([])
          onFinish(true)
        })
      searchCountries(newFilter)
        .then(newCountries => {
          setCountries(newCountries)
          onFinish(false)
        })
        .catch(error => {
          console.error(error)
          setCountries([])
          onFinish(true)
        })
    })
  React.useEffect(() => {
    if (filter.length < MIN_FILTER_LENGTH) {
      clearData()
      return
    }
    loadData(filter)
  }, [filter])
  return (
    <Autocomplete
      id="shape-select"
      options={isLoading ? [] : [...cities, ...postalCodes, ...states, ...countries, ...metropolitans]}
      value={null}
      loading={isLoading}
      size="small"
      getOptionLabel={shape => getShapeTitle({ shape })}
      loadingText={translate("label.search.in-progress")}
      noOptionsText={translate(
        filter.length < MIN_FILTER_LENGTH ? "label.min.char" : "label.no.match",
        MIN_FILTER_LENGTH
      )}
      onInputChange={(_event, newFilter) => {
        clearTimeout(timeout)
        timeout = setTimeout(() => setFilter(newFilter), 600)
      }}
      onChange={(_event, newShape) => {
        if (newShape) {
          onSelect(newShape)
        }
      }}
      filterOptions={options => options} // no need to filter, options are already filtered
      onSelect={() => setFilter("")}
      onClose={() => setFilter("")}
      groupBy={option => translationsByType[option.type]}
      renderInput={params => (
        <TextField
          {...params}
          label={translate("campaign.geo-targets.shape.search")}
          InputProps={{
            ...params.InputProps,
            endAdornment: isLoading ? (
              <FlexBox
                sx={{
                  "position": "relative",
                  "& > *": { position: "relative", width: "1.5rem" },
                  "& .MuiAutocomplete-endAdornment": { right: "0 !important" }
                }}>
                {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </FlexBox>
            ) : (
              params.InputProps.endAdornment
            )
          }}
        />
      )}
    />
  )
})
